import React from 'react';
import ActivityLoader from '../Common/ActivityLoader';
import Notification from '../Common/Notification';
import { useTheme } from '../../DataStore/ThemeContext';
import { useSchoolTransactions } from '../../DataStore/FeesTransactionContext';
import functions from '../../util/functions';
import TransactionMgtHead from './TransactionMgtHead';
import { useNavigate } from 'react-router-dom';
import { useFeesProfile } from '../../DataStore/FeesProfileContext';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const TransactionList = () => {
    const { transactionListing, searchText, selectedIds, setSelectedIds, loading } = useSchoolTransactions();
    const {dispatch} = useFeesProfile();
    const {openSettings} = useTheme();
    const navigate = useNavigate();
    

    // Filtered students based on searchText
    const filteredTransactions = Array.isArray(transactionListing)
        ? transactionListing.filter(
              (transaction) =>
                  transaction.student.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                  transaction.student.reg_no?.toLowerCase().includes(searchText.toLowerCase()) ||
                  transaction.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                  transaction.amount?.toLowerCase().includes(searchText.toLowerCase()) ||
                  transaction.ledger.transaction_ref?.toLowerCase().includes(searchText.toLowerCase())

          )
        : [];

    // Toggle selection for a transaction
    const toggleSelection = (id) => {
        setSelectedIds(
            selectedIds.includes(id)
                ? selectedIds.filter((selectedId) => selectedId !== id) // Remove if already selected
                : [...selectedIds, id] // Add if not selected
        );
    };

    // Check if a transaction is selected
    const isSelected = (id) => selectedIds.includes(id);

    const handleLedgerId = (record) =>{
        openSettings("Fees Transaction", "TransactionSearchResult", record);
    }

    const handleStudent = (id) =>{
        dispatch({type: 'studentId', payload: id})
        navigate('/school-fees-pstudent');
    }

    const renderTrans=(bill_type)=>{
        switch (bill_type*1) {
            case 1:
                return  <span className="badge badge-warning">Invoice</span>
            case 2:
                return  <span className="badge badge-secondary">Discount</span>
            default:
                return <span className="badge badge-success">Payment</span>
        }
    }


    const exportTransactionsToExcel = ({ onlySelected = false } = {}) => {
        const rows = onlySelected
            ? filteredTransactions.filter((t) => selectedIds.includes(t.ledger_id))
            : filteredTransactions;

        if (!rows || rows.length === 0) return;

        const data = rows.map((t, idx) => ({
            "#": idx + 1,
            "Trans Date": functions.formatDate(t.trans_date),
            "Student Name": t?.student?.full_name || "",
            "Reg No.": t?.student?.reg_no || "",
            "Class": t?.class?.class_name || "",
            "Ledger": `JE${t.ledger_id}`,
            "Trans Type":
            Number(t.billing_type) === 1
                ? "Invoice"
                : Number(t.billing_type) === 2
                ? "Discount"
                : "Payment",
            "Amount": Number(t.amount || 0),
            "Description": t?.description || "",
            "Reference": t?.ledger?.transaction_ref || "",
            "Record Date": functions.formatDate(t.created_at),
            "Recorded By": t?.ledger?.created_by?.username || "",
        }));

        const ws = XLSX.utils.json_to_sheet(data);

        // Optional: make it readable
        ws["!cols"] = [
            { wch: 4 },   // #
            { wch: 12 },  // Trans Date
            { wch: 26 },  // Student Name
            { wch: 14 },  // Reg No
            { wch: 12 },  // Class
            { wch: 10 },  // Ledger
            { wch: 12 },  // Trans Type
            { wch: 14 },  // Amount
            { wch: 28 },  // Description
            { wch: 16 },  // Reference
            { wch: 12 },  // Record Date
            { wch: 14 },  // Recorded By
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transactions");

        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const today = new Date().toISOString().slice(0, 10);

        const suffix = onlySelected ? "Selected" : "All";
        const filename = `School_Fees_Transactions_${suffix}_${today}.xlsx`;

        saveAs(new Blob([out], { type: "application/octet-stream" }), filename);
    };


    return (
        <>
        <TransactionMgtHead handleExport={() => exportTransactionsToExcel()} />

        <div className="card">
            {/* Table */}
            <div className="card-body">
                <div className='table-responsive'>
                <table className="table table-hover">
                    <thead className="table-light">
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            // Select all students
                                            setSelectedIds(filteredTransactions.map((transaction) => transaction.ledger_id));
                                        } else {
                                            // Deselect all students
                                            setSelectedIds([]);
                                        }
                                    }}
                                    checked={
                                        filteredTransactions.length > 0 &&
                                        selectedIds.length === filteredTransactions.length
                                    }
                                />
                            </th>
                            <th className='nowrap'>Trans Date</th>
                            <th className='nowrap'>Student Name</th>
                            <th className='nowrap'>Reg No.</th>
                            <th className='nowrap'>Class</th>
                            <th className='nowrap'>Ledger</th>
                            <th className='nowrap'>Trans</th>
                            <th className='nowrap'>Amount</th>
                            <th className='nowrap'>Description</th>
                            <th className='nowrap'>Reference</th>
                            <th className='nowrap'>Record date</th>
                            <th className='nowrap'>Recorded by</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(filteredTransactions) &&
                            filteredTransactions.map((transaction, key) => (
                                <tr
                                    key={key}
                                    className={isSelected(transaction.ledger_id) ? 'selected-row' : ''}
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={isSelected(transaction.ledger_id)}
                                            onChange={() => toggleSelection(transaction.ledger_id)}
                                        />
                                    </td>
                                    <td className='nowrap'>{functions.formatDate(transaction.trans_date)}</td>
                                    <td>
                                        <span className='link text-primary' onClick={()=>handleStudent(transaction.student.id)}>
                                            {transaction.student.full_name}
                                        </span>
                                    </td>
                                    <td>{transaction.student.reg_no}</td>
                                    <td>{transaction.class?.class_name || ''}</td>
                                    <td>
                                        <span className='link text-primary' onClick={()=>handleLedgerId(transaction.ledger)}>
                                            JE{transaction.ledger_id}
                                        </span>
                                    </td>
                                    <td>{renderTrans(transaction.billing_type)}</td>
                                    <td>{functions.numberFormat(transaction.amount)}</td>
                                    <td>{transaction.description}</td>
                                    <td>{transaction.ledger.transaction_ref}</td>
                                    <td>{functions.formatDate(transaction.created_at)}</td>
                                    <td>{transaction.ledger.created_by.username}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {filteredTransactions.length === 0 && !loading && (
                    <Notification message="No matching entries found, please apply filter to perform a deep search." closable={false} />
                )}
                {loading && <ActivityLoader />}
                {transactionListing === "404" && <Notification message="No school fees transaction found." closable={false} />}
                </div>
            </div>
        </div>
    </>
    );
};

export default TransactionList;
